import { EcoaContent } from "@components/common/EconContent";
import React, { useEffect, useRef, useState } from "react";
import { IdentificationResponseData } from "maven-lib/dist/dto";
import { notify } from "@coralblack/flax";
import { Footer } from "@layouts/Footer";
import { InitialCrfPageSequence, InitialVisitSequence, PreGroupId } from "maven-lib/dist/consts/edc";
import { StudyCaptureData } from "maven-lib/dist/dto/entity/studyCapture";
import { IdentificationFrameButtonByDanal } from "../../../components/common/IdentificationFrameButtonByDanal";
import { HbButton } from "@maven-surface/components/base/HbButton";
import { HbDivider } from "@maven-surface/components/base/HbDivider";
import { HbParagraph } from "@maven-surface/components/base/HbParagraph";
import { useRoute } from "@maven-surface/hooks/useRoute";
import { ECOA_ACCESS_CODE_LANG } from "@maven-msg/lang/ecoaAccessCode";
import { ECOA_CAPTURE_LANG } from "@maven-msg/lang/ecoaCapture";
import { MvnMessage } from "@maven-msg/lib/Message";

interface IdentificationProps {
  studyId: bigint;
  onSaveData: (data: Array<StudyCaptureData>) => void;
  onSaveEncryptedPayload: (encryptedPayload: string) => void;
}

export function Identification({ studyId, onSaveData, onSaveEncryptedPayload }: IdentificationProps) {
  const [isDirtyIdentificationButton, setIsDirtyIdentificationButton] = useState<boolean>(false);
  const frameContainer = useRef<HTMLDivElement>(null);
  const { pushPath } = useRoute();

  const callbackUrl = `${process.env.REACT_APP_MAVEN_ECOA_URL}`;
  const frameContainerId = "danalFrameContainer";
  const frameId = "danalFrame";

  const callbackFunction = (responseData: IdentificationResponseData) => {
    if (!responseData) return;

    onSaveEncryptedPayload(responseData.encrypted);
    onSaveData(
      ["name", "dateOfBirth", "phone", "sex"].map((item) => ({
        id: {
          groupId: PreGroupId,
          folderId: "PRE",
          visitId: "PRE",
          visitSeq: InitialVisitSequence,
          crfPageId: "PREPROF",
          crfPageSeq: InitialCrfPageSequence,
          sectionId: "PREPROF",
          itemId: item === "dateOfBirth" ? "PREBIRTH" : item === "phone" ? "PRECONTACT" : `PRE${item.toUpperCase()}`,
          itemSeq: 1,
        },
        val: {
          dbVal: item === "sex" ? Number(responseData[item]) : responseData[item],
          calcVal: null,
          removed: false,
          silent: false,
        },
      }))
    );

    if (responseData?.result === "success") {
      pushPath(`/${studyId}/pre/pre-screening`);
    } else {
      setIsDirtyIdentificationButton(false);
      notify(MvnMessage(`${responseData.returnMsg} (${responseData.returnCode})`), "ERROR");
    }
  };

  const handleClickButton = () => {
    setIsDirtyIdentificationButton(true);
  };

  useEffect(() => {
    window?.scrollTo(0, 0);
  }, []);

  return (
    <>
      <div id={frameContainerId} ref={frameContainer} style={{ touchAction: "none" }} />
      <div style={{ display: isDirtyIdentificationButton ? "none" : "block" }}>
        <EcoaContent>
          <HbParagraph
            text={MvnMessage(ECOA_ACCESS_CODE_LANG.ECOA_AUTH_PRESS_BUTTON_TEXT_LABEL)}
            size="3X-LARGE"
            bold={true}
            color="GRAY"
          />
          <HbDivider space={2} />
          <div style={{ textAlign: "center" }}>
            <IdentificationFrameButtonByDanal
              buttonId={"identificationButton"}
              buttonText={MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_VERIFY)}
              targetFrameId={frameId}
              frameContainer={frameContainer}
              callbackUrl={callbackUrl}
              callbackFunction={callbackFunction}
              onPrepareIdentification={handleClickButton}
            />
          </div>
        </EcoaContent>
      </div>
      {!isDirtyIdentificationButton && (
        <Footer>
          <HbButton text={MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_NEXT)} disabled />
        </Footer>
      )}
    </>
  );
}
